// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { DAY_MINUTES, type ScheduleSettings, type ScheduleStrategy, type TimelineSegment } from "./types";

/** Format minutes-since-midnight as `HH:MM`. Clamps to 24:00 for the day end. */
export function formatMinutes(value: number): string {
  const clamped = Math.max(0, Math.min(DAY_MINUTES, Math.round(value)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Camera operating window the derived strategies are laid out within. */
export interface CaptureWindow {
  start: number;
  end: number;
}

const FULL_DAY: CaptureWindow = { start: 0, end: DAY_MINUTES };

/**
 * Derive the `no_of_images` clock layout within the camera window: the first slot is always
 * 1 hour, then the rest of the window is split into `n - 1` equal slots. A single image fills
 * the whole window.
 */
export function noOfImagesRanges(n: number, window: CaptureWindow = FULL_DAY): [number, number][] {
  const { start, end } = window;
  if (n <= 1) return [[start, end]];
  const firstEnd = Math.min(start + 60, end);
  const first: [number, number] = [start, firstEnd];
  const rest: [number, number][] = [];
  const slot = (end - firstEnd) / (n - 1);
  for (let i = 0; i < n - 1; i++) {
    rest.push([Math.round(firstEnd + slot * i), Math.round(firstEnd + slot * (i + 1))]);
  }
  return [first, ...rest];
}

/** Hourly windows stepping from the window start up to its end (last may be partial). */
function everyHourRanges(window: CaptureWindow): [number, number][] {
  const { start, end } = window;
  const ranges: [number, number][] = [];
  for (let s = start; s < end; s += 60) {
    ranges.push([s, Math.min(s + 60, end)]);
  }
  return ranges;
}

/**
 * Adapt any schedule shape into timeline segments for rendering. `window` is the camera
 * on/off window that anchors the derived strategies (`every_hour`, `no_of_images`).
 */
export function toSegments(settings: ScheduleSettings, window: CaptureWindow = FULL_DAY): TimelineSegment[] {
  switch (settings.type) {
    case "every_hour":
      return everyHourRanges(window).map(([start, end]) => ({ start, end, label: settings.labelType }));
    case "no_of_images":
      return noOfImagesRanges(settings.noOfImages, window).map(([start, end], i) => ({
        start,
        end,
        label: settings.labelTypes[i] ?? settings.labelTypes[0] ?? "model",
      }));
    case "static":
      return settings.timeRanges.map(([start, end], i) => ({
        start,
        end,
        label: settings.labelTypes[i] ?? "model",
      }));
    case "strict":
      return settings.timeSlots.map((t, i) => ({
        start: t,
        end: t,
        label: settings.labelTypes[i] ?? "model",
      }));
  }
}

/**
 * Convert edited segments back into a schedule of the given strategy. `prev` preserves
 * fields the timeline doesn't own (e.g. `awakeTime`). For `every_hour`/`no_of_images`
 * positions are derived, so only labels (and count) round-trip.
 */
export function fromSegments(
  strategy: ScheduleStrategy,
  segments: TimelineSegment[],
  prev: ScheduleSettings,
): ScheduleSettings {
  const awakeTime = "awakeTime" in prev ? prev.awakeTime : 1;
  switch (strategy) {
    case "every_hour":
      return { type: "every_hour", labelType: segments[0]?.label ?? "model", awakeTime };
    case "no_of_images":
      return {
        type: "no_of_images",
        noOfImages: segments.length || 1,
        labelTypes: segments.map((s) => s.label),
        awakeTime,
      };
    case "static":
      return {
        type: "static",
        timeRanges: segments.map((s) => [s.start, s.end] as [number, number]),
        labelTypes: segments.map((s) => s.label),
        awakeTime,
      };
    case "strict":
      return {
        type: "strict",
        timeSlots: segments.map((s) => s.start),
        labelTypes: segments.map((s) => s.label),
      };
  }
}

/** Seed a sensible default schedule when switching strategy or starting fresh. */
export function defaultSettings(
  strategy: ScheduleStrategy,
  awakeTime = 1,
  window: CaptureWindow = FULL_DAY,
): ScheduleSettings {
  switch (strategy) {
    case "every_hour":
      return { type: "every_hour", labelType: "model", awakeTime };
    case "no_of_images":
      return { type: "no_of_images", noOfImages: 12, labelTypes: Array(12).fill("model"), awakeTime };
    case "static": {
      // Seed explicit hourly windows that replicate the every_hour layout within the camera window.
      const timeRanges = everyHourRanges(window);
      return { type: "static", timeRanges, labelTypes: timeRanges.map(() => "model"), awakeTime };
    }
    case "strict":
      return { type: "strict", timeSlots: [360, 540, 720, 900, 1080], labelTypes: Array(5).fill("model") };
  }
}

/** Whether a `static` schedule has overlapping or inverted windows (invalid). */
export function findStaticOverlaps(timeRanges: [number, number][]): number[] {
  const bad = new Set<number>();
  const order = timeRanges.map((r, i) => ({ r, i })).sort((a, b) => a.r[0] - b.r[0]);
  for (let k = 0; k < order.length; k++) {
    const cur = order[k];
    if (!cur) continue;
    if (cur.r[1] <= cur.r[0]) bad.add(cur.i);
    const prev = order[k - 1];
    if (prev && cur.r[0] < prev.r[1]) {
      bad.add(cur.i);
      bad.add(prev.i);
    }
  }
  return [...bad];
}
